import React, {useCallback, useEffect, useMemo, useState} from 'react'
import Dialog from 'components/Display/Dialogs/Dialog'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {
    useGetPermissionsCatalogQuery,
    useGetUserAccountPermissionsQuery,
    useGetUserQuery,
    useSetUserCustomPermissionMutation,
} from 'store/Store'
import {getDocumentIdAsString} from 'utils'
import {toast} from 'sonner'
import {showConfirmAlert} from 'services/alert'
import Autocomplete from 'components/Control/Autocomplete'

const ACTION_ORDER = ['CREATE', 'READ', 'UPDATE', 'DELETE']
const RESERVED_KEYS = new Set(['ALL', 'NONE'])
const HIDDEN_RESOURCES = new Set(['DEFAULT'])

const normalizeRule = (rule, resourceCatalog = {}) => {
    if (!resourceCatalog) return 0
    if (rule === 'ALL') return resourceCatalog.ALL ?? 0
    if (rule === 'NONE') return resourceCatalog.NONE ?? 0
    const actions = Array.isArray(rule) ? rule : [rule]
    return actions.reduce((acc, action) => acc | (resourceCatalog[action] ?? 0), 0)
}

const getActionKeys = (resourceCatalog = {}) => {
    const ordered = ACTION_ORDER.filter((action) => typeof resourceCatalog[action] === 'number')
    const dynamic = Object.keys(resourceCatalog).filter(
        (key) => !RESERVED_KEYS.has(key) && !ACTION_ORDER.includes(key)
    )
    return [...ordered, ...dynamic]
}

const createSafeEvent = (event) => {
    if (event && typeof event.stopPropagation === 'function') return event
    return {stopPropagation: () => {}}
}

const UserPermissionsDialog = ({open, onClose, userId, userSummary}) => {
    const {t} = useTranslation()
    const [selectedAccountId, setSelectedAccountId] = useState(null)
    const [selectedResource, setSelectedResource] = useState(null)
    const [draftValue, setDraftValue] = useState(null)

    const shouldFetchUser = open && Boolean(userId)
    const {data: userDetails, isFetching: isUserLoading} = useGetUserQuery(userId, {
        skip: !shouldFetchUser,
    })
    const {data: catalog, isFetching: isCatalogLoading} = useGetPermissionsCatalogQuery(undefined, {
        skip: !open,
    })

    const accountOptions = useMemo(() => {
        const accounts = userDetails?.userAccounts || userSummary?.userAccounts || []
        return accounts
            .map((userAccount) => {
                const id = getDocumentIdAsString(userAccount?.account)
                if (!id) return null
                return {
                    _id: id,
                    id,
                    name: userAccount?.account?.name || t('permissions.unknownAccount'),
                    role: userAccount?.role,
                }
            })
            .filter(Boolean)
    }, [t, userDetails?.userAccounts, userSummary?.userAccounts])

    useEffect(() => {
        if (!open) {
            setSelectedAccountId(null)
            setSelectedResource(null)
            setDraftValue(null)
            return
        }

        if (!accountOptions.length) {
            setSelectedAccountId(null)
            return
        }

        if (
            !selectedAccountId ||
            !accountOptions.some((option) => option.id === selectedAccountId)
        ) {
            setSelectedAccountId(accountOptions[0].id)
        }
    }, [accountOptions, open, selectedAccountId])

    const shouldFetchPermissions = open && Boolean(userId) && Boolean(selectedAccountId)

    const {
        data: permissionPayload,
        isFetching: isPermissionLoading,
        error: permissionsError,
    } = useGetUserAccountPermissionsQuery(
        {userId, accountId: selectedAccountId},
        {skip: !shouldFetchPermissions}
    )

    const [setCustomPermission, {isLoading: isSaving}] = useSetUserCustomPermissionMutation()

    useEffect(() => {
        setDraftValue(null)
    }, [permissionPayload, selectedAccountId])

    const customPermissions = useMemo(() => {
        const raw = permissionPayload?.customPermissions || {}
        return Object.entries(raw).reduce((acc, [resource, value]) => {
            const resourceCatalog = catalog?.[resource]
            acc[resource] =
                typeof value === 'number' ? value : normalizeRule(value, resourceCatalog || {})
            return acc
        }, {})
    }, [catalog, permissionPayload?.customPermissions])

    const resourceKeys = useMemo(() => {
        if (!catalog) return []
        return Object.keys(catalog)
            .filter((resource) => !HIDDEN_RESOURCES.has(resource))
            .sort()
    }, [catalog])

    const resourceOptions = useMemo(
        () =>
            resourceKeys.map((resource) => ({
                _id: resource,
                name: t(`permissions.resources.${resource}`, {defaultValue: resource}),
                key: resource,
            })),
        [resourceKeys, t]
    )

    useEffect(() => {
        if (!resourceKeys.length) {
            setSelectedResource(null)
            setDraftValue(null)
            return
        }

        if (!selectedResource || !resourceKeys.includes(selectedResource)) {
            setSelectedResource(resourceKeys[0])
            setDraftValue(null)
        }
    }, [resourceKeys, selectedResource])

    const selectedAccountOption = useMemo(
        () => accountOptions.find((option) => option.id === selectedAccountId) || null,
        [accountOptions, selectedAccountId]
    )

    const selectedResourceOption = useMemo(
        () => resourceOptions.find((option) => option.key === selectedResource) || null,
        [resourceOptions, selectedResource]
    )

    const accountRole = permissionPayload?.role || selectedAccountOption?.role || null
    const baseValue = selectedResource ? customPermissions[selectedResource] ?? 0 : 0
    const currentValue = draftValue ?? baseValue
    const hasUnsaved = draftValue !== null
    const isLoading = isUserLoading || isCatalogLoading
    const isMatrixLoading = isPermissionLoading || isLoading
    const disableInputs = !selectedResource || isMatrixLoading || isSaving
    const actions = selectedResource ? getActionKeys(catalog?.[selectedResource]) : []

    const askToDiscard = useCallback(
        (event, next) => {
            if (!hasUnsaved) {
                next()
                return
            }

            showConfirmAlert({
                e: createSafeEvent(event),
                text: t('permissions.confirm.unsaved'),
                onConfirm: () => next(),
                disableSuccessAlert: true,
                t,
            })
        },
        [hasUnsaved, t]
    )

    const handleAccountSelect = useCallback(
        (event, option) => {
            const nextAccount = option?.id
            if (!nextAccount || nextAccount === selectedAccountId) return

            askToDiscard(event, () => {
                setSelectedAccountId(nextAccount)
                setSelectedResource(null)
                setDraftValue(null)
            })
        },
        [askToDiscard, selectedAccountId]
    )

    const handleResourceSelect = useCallback(
        (event, option) => {
            const nextResource = option?.key
            if (!nextResource || nextResource === selectedResource) return

            askToDiscard(event, () => {
                setSelectedResource(nextResource)
                setDraftValue(null)
            })
        },
        [askToDiscard, selectedResource]
    )

    const handleToggle = useCallback(
        (actionKey, enabled) => {
            if (!selectedResource) return
            const resourceCatalog = catalog?.[selectedResource]
            const actionValue = resourceCatalog?.[actionKey]
            if (typeof actionValue !== 'number') return

            const nextValue = enabled ? currentValue | actionValue : currentValue & ~actionValue
            if (nextValue === baseValue) {
                setDraftValue(null)
            } else {
                setDraftValue(nextValue)
            }
        },
        [baseValue, catalog, currentValue, selectedResource]
    )

    const persistValue = useCallback(
        (value, toastKey) => {
            if (!selectedResource || !selectedAccountId || !userId) return

            const request = setCustomPermission({
                userId,
                accountId: selectedAccountId,
                resource: selectedResource,
                permissions: value,
            })
                .unwrap()
                .then(() => setDraftValue(null))

            toast.promise(request, {
                loading: t('loading'),
                success: () => t(`permissions.toast.${toastKey}.success`),
                error: (err) => err?.data?.error || t(`permissions.toast.${toastKey}.error`),
            })
        },
        [selectedAccountId, selectedResource, setCustomPermission, t, userId]
    )

    const handleSave = useCallback(() => {
        if (draftValue === null) return
        persistValue(draftValue, 'save')
    }, [draftValue, persistValue])

    const handleClose = useCallback(() => {
        askToDiscard(null, () => onClose?.())
    }, [askToDiscard, onClose])

    const userName = useMemo(() => {
        const source = userDetails || userSummary
        if (!source) return ''
        return [source.firstName, source.lastName].filter(Boolean).join(' ')
    }, [userDetails, userSummary])
    const userEmail = userDetails?.email || userSummary?.email || ''

    const renderPermissions = () => {
        if (isMatrixLoading) {
            return (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                </Box>
            )
        }

        if (!selectedResource) {
            return (
                <Alert severity="info">
                    {t('permissions.emptyState.selectResource', {
                        defaultValue: 'Select a resource to review its permissions.',
                    })}
                </Alert>
            )
        }

        if (!actions.length) {
            return (
                <Alert severity="info">
                    {t('permissions.emptyState.noActions', {
                        defaultValue: 'No configurable actions for this resource.',
                    })}
                </Alert>
            )
        }

        return (
            <Paper variant="outlined" sx={{p: 2}}>
                <Stack spacing={2}>
                    <Typography variant="subtitle2">
                        {t(`permissions.resources.${selectedResource}`, {
                            defaultValue: selectedResource,
                        })}
                    </Typography>

                    <Stack spacing={1}>
                        {actions.map((action) => (
                            <FormControlLabel
                                key={`${selectedResource}_${action}`}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={Boolean(
                                            currentValue &
                                                (catalog?.[selectedResource]?.[action] ?? 0)
                                        )}
                                        onChange={(event) =>
                                            handleToggle(action, event.target.checked)
                                        }
                                        disabled={disableInputs}
                                    />
                                }
                                label={t(`permissions.actionLabels.${action}`, {
                                    defaultValue: action,
                                })}
                            />
                        ))}
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSave}
                            disabled={disableInputs || draftValue === null}>
                            {t('permissions.actions.save')}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        )
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            title={t('permissions.title')}
            withFormAction={false}
            scroll="paper">
            <Stack spacing={2} py={1}>
                <Box>
                    <Typography variant="overline" color="text.secondary">
                        {t('permissions.userLabel')}
                    </Typography>
                    <Typography variant="h6">{userName || t('permissions.unknownUser')}</Typography>
                    {userEmail && (
                        <Typography variant="body2" color="text.secondary">
                            {userEmail}
                        </Typography>
                    )}
                </Box>

                {!accountOptions.length && !isLoading && (
                    <Alert severity="warning">{t('permissions.noAccounts')}</Alert>
                )}

                {permissionsError && (
                    <Alert severity="error">
                        {permissionsError?.data?.error || t('permissions.loadError')}
                    </Alert>
                )}

                <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                    <Autocomplete
                        label={t('permissions.accountLabel')}
                        options={accountOptions}
                        value={selectedAccountOption}
                        onChange={handleAccountSelect}
                        isFilter={false}
                        disableClearable
                        disabled={!accountOptions.length}
                        isLoading={isLoading}
                        fullWidth
                    />
                    <Autocomplete
                        label={t('permissions.resourcesLabel', {defaultValue: 'Resource'})}
                        options={resourceOptions}
                        value={selectedResourceOption}
                        onChange={handleResourceSelect}
                        isFilter={false}
                        disableClearable
                        disabled={!resourceOptions.length || isMatrixLoading}
                        isLoading={isMatrixLoading}
                        fullWidth
                    />
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        {t('permissions.roleLabel')}
                    </Typography>
                    <Paper variant="outlined" sx={{px: 1, py: 0.5}}>
                        <Typography variant="body2">
                            {accountRole
                                ? t(`selectOptions.${accountRole}`)
                                : t('permissions.roleFallback')}
                        </Typography>
                    </Paper>
                </Stack>

                {permissionPayload?.isSuper && (
                    <Alert severity="info">{t('permissions.superUserNotice')}</Alert>
                )}

                {renderPermissions()}
            </Stack>
        </Dialog>
    )
}

export default UserPermissionsDialog
